/** @format — Voice-note pulse: real mic when possible + randomized ChatGPT-style fallback */

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

const DOCKIE_WAVE_COLOR = "#2563EB";

/**
 * Hybrid waveform for voice notes:
 * - Prefers live mic RMS when the browser allows a second analyser stream
 * - Falls back to irregular randomized speech-like bursts (no obvious loop)
 * - Flat dots until the first activity (ChatGPT-style)
 */
export function VoiceNoteWaveform({
  animated = true,
  active,
}: {
  animated?: boolean;
  stream?: MediaStream | null;
  active?: boolean;
}) {
  const isListening = Boolean(active);
  const isProcessing = !isListening && animated;

  if (!isListening && !isProcessing) return null;

  return (
    <div className="flex h-7 min-w-0 w-full flex-1 items-center overflow-hidden">
      <HybridSpeechPulse mode={isListening ? "listening" : "processing"} />
    </div>
  );
}

function HybridSpeechPulse({ mode }: { mode: "listening" | "processing" }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const barWidth = 2;
    const barGap = 2;
    const barRadius = 1;
    const waveHeight = 28;
    const startedAt = performance.now();
    const flatMs = mode === "listening" ? 900 : 0;

    let raf = 0;
    let history: number[] = [];
    let lastPush = 0;
    let seed = Math.random() * 10_000;

    // Randomized phrase schedule (no fixed cycle)
    let phraseEnd = 0;
    let quietEnd = startedAt + flatMs;
    let phraseShape = 0;

    // Live mic (best-effort — may be silent while MediaRecorder holds the device)
    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let micStream: MediaStream | null = null;
    let timeDomain: Uint8Array<ArrayBuffer> | null = null;
    let freqDomain: Uint8Array<ArrayBuffer> | null = null;

    const rand = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    const scheduleNextPhrase = (now: number) => {
      // Irregular quiet gap: 0.4s – 2.2s
      const quiet = 400 + rand() * 1800;
      quietEnd = now + quiet;
      // Irregular phrase length: 0.35s – 1.8s
      const length = 350 + rand() * 1450;
      phraseEnd = quietEnd + length;
      // Pick a different envelope flavor each phrase
      phraseShape = Math.floor(rand() * 5);
    };

    scheduleNextPhrase(startedAt + flatMs);

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const h = Math.min(waveHeight, rect.height || waveHeight);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const readMicLevel = () => {
      if (!analyser || !timeDomain || !freqDomain) return 0;
      if (audioContext?.state === "suspended") void audioContext.resume();

      analyser.getByteTimeDomainData(timeDomain);
      analyser.getByteFrequencyData(freqDomain);

      let sumSquares = 0;
      for (let i = 0; i < timeDomain.length; i += 1) {
        const sample = (timeDomain[i] - 128) / 128;
        sumSquares += sample * sample;
      }
      const rms = Math.sqrt(sumSquares / timeDomain.length);

      let spectral = 0;
      const usable = Math.floor(freqDomain.length * 0.5);
      for (let i = 1; i < usable; i += 1) spectral += freqDomain[i];
      spectral = usable ? spectral / (usable * 255) : 0;

      // Gate noise floor so silence stays flat
      const level = Math.min(1, rms * 4.2 + spectral * 0.9);
      return level < 0.04 ? 0 : level;
    };

    const simulatedLevel = (now: number) => {
      if (mode === "processing") {
        const t = now / 1000;
        // Soft irregular motion — not a single sine
        return (
          0.18 +
          Math.abs(Math.sin(t * 1.3 + seed)) * 0.22 +
          Math.abs(Math.sin(t * 2.7 + seed * 0.3)) * 0.16 +
          rand() * 0.08
        );
      }

      if (now - startedAt < flatMs) return 0.04;

      if (now >= phraseEnd) scheduleNextPhrase(now);
      if (now < quietEnd) return 0.04 + rand() * 0.015;

      const phraseLen = Math.max(1, phraseEnd - quietEnd);
      const p = (now - quietEnd) / phraseLen; // 0 → 1 through phrase

      let envelope = 0;
      switch (phraseShape) {
        case 0: // soft attack, long tail
          envelope = Math.pow(Math.sin(Math.PI * Math.min(1, p * 1.15)), 1.4);
          break;
        case 1: // sharp mid spike
          envelope = Math.exp(-Math.pow((p - 0.45) * 4.2, 2));
          break;
        case 2: // two humps
          envelope =
            0.55 * Math.exp(-Math.pow((p - 0.28) * 5, 2)) +
            0.85 * Math.exp(-Math.pow((p - 0.72) * 4.5, 2));
          break;
        case 3: // rising then cut
          envelope = p < 0.7 ? Math.pow(p / 0.7, 0.7) : Math.max(0, 1 - (p - 0.7) / 0.3);
          break;
        default: // noisy plateaus
          envelope = 0.35 + 0.55 * (0.5 + 0.5 * Math.sin(p * Math.PI * (2 + rand())));
          break;
      }

      const texture =
        0.55 +
        rand() * 0.45 +
        Math.abs(Math.sin(now / 70 + seed)) * 0.2 * rand();

      return Math.min(0.85, Math.max(0.06, envelope * texture * (0.45 + rand() * 0.4)));
    };

    const nextLevel = (now: number) => {
      const live = readMicLevel();
      if (live > 0.05) {
        // Real speech wins whenever we can hear it
        return Math.min(0.9, Math.max(0.08, live * 1.35));
      }
      return simulatedLevel(now);
    };

    const draw = (now: number) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        raf = requestAnimationFrame(draw);
        return;
      }

      const rect = container.getBoundingClientRect();
      const width = rect.width;
      const height = Math.min(waveHeight, rect.height || waveHeight);
      if (width <= 0) {
        raf = requestAnimationFrame(draw);
        return;
      }

      const step = barWidth + barGap;
      const barCount = Math.max(8, Math.ceil(width / step) + 2);
      while (history.length < barCount) history.unshift(0.04);

      // Slower scroll (~48ms per bar) so pulses feel less frantic
      if (!lastPush || now - lastPush > 48) {
        history.push(nextLevel(now));
        while (history.length > barCount) history.shift();
        lastPush = now;
      }

      ctx.clearRect(0, 0, width, height);
      const midY = height / 2;

      for (let i = 0; i < barCount; i++) {
        const value = history[history.length - 1 - i] ?? 0.04;
        const x = width - (i + 1) * step;
        if (x + barWidth < 0) continue;

        const barHeight = Math.max(2.5, value * height * 0.9);
        const y = midY - barHeight / 2;

        ctx.fillStyle = DOCKIE_WAVE_COLOR;
        ctx.globalAlpha = 0.4 + value * 0.55;
        ctx.beginPath();
        if (typeof ctx.roundRect === "function") {
          ctx.roundRect(x, y, barWidth, barHeight, barRadius);
        } else {
          ctx.rect(x, y, barWidth, barHeight);
        }
        ctx.fill();
      }

      const fade = Math.min(14, width * 0.07);
      const gradient = ctx.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, "rgba(255,255,255,1)");
      gradient.addColorStop(fade / width, "rgba(255,255,255,0)");
      gradient.addColorStop(1 - fade / width, "rgba(255,255,255,0)");
      gradient.addColorStop(1, "rgba(255,255,255,1)");
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;

      raf = requestAnimationFrame(draw);
    };

    const setupMic = async () => {
      if (mode !== "listening") return;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        const Ctx =
          window.AudioContext ||
          (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        audioContext = new Ctx();
        if (audioContext.state === "suspended") await audioContext.resume();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.55;
        analyser.minDecibels = -90;
        analyser.maxDecibels = -20;
        audioContext.createMediaStreamSource(micStream).connect(analyser);
        timeDomain = new Uint8Array(analyser.fftSize) as Uint8Array<ArrayBuffer>;
        freqDomain = new Uint8Array(analyser.frequencyBinCount) as Uint8Array<ArrayBuffer>;
      } catch {
        // Simulation-only fallback — still looks alive
      }
    };

    void setupMic();
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      micStream?.getTracks().forEach((track) => track.stop());
      void audioContext?.close();
      micStream = null;
      audioContext = null;
      analyser = null;
    };
  }, [mode]);

  return (
    <div ref={containerRef} className={cn("relative h-7 w-full min-w-0 flex-1")}>
      <canvas ref={canvasRef} className="block h-full w-full" aria-hidden />
    </div>
  );
}

export function VoiceNoteActivityDots() {
  return (
    <div className="flex flex-shrink-0 items-center gap-[3px]">
      {Array.from({ length: 7 }).map((_, index) => (
        <div
          key={index}
          className="voice-note-dot h-[3px] w-[3px] rounded-full bg-[#60A5FA]"
          style={{ animationDelay: `${index * 120}ms` }}
        />
      ))}
    </div>
  );
}
